import { ApiError, type SmsInvite } from "@rhema/client";

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** A day as "today" or "on 2 November", with the year when it is not this year. */
function onDay(when: Date, now: Date): string {
  if (sameDay(when, now)) return "today";
  const options: Intl.DateTimeFormatOptions = { day: "numeric", month: "long" };
  if (when.getFullYear() !== now.getFullYear()) options.year = "numeric";
  return `on ${when.toLocaleDateString("en-GB", options)}`;
}

/** What the invite sheet says about the last text Rhema sent to this number for you. */
export function lastTextNote(invite: SmsInvite, now = new Date()): string {
  const sent = `You texted an invite to this number ${onDay(new Date(invite.created_at), now)}.`;
  if (invite.delivery === "failed")
    return `${sent} The text was not delivered. You can send another text or share the link another way.`;
  if (invite.delivery === "delivered") return `${sent} The text was delivered.`;
  return sent;
}

/** A time to come back, as "in 40 seconds", "at 14:05" or "tomorrow at 09:30". */
export function retryWhen(retryAt: string, now = new Date()): string {
  const at = new Date(retryAt);
  const seconds = Math.max(1, Math.ceil((at.getTime() - now.getTime()) / 1000));
  if (seconds < 90) return `in ${seconds} ${seconds === 1 ? "second" : "seconds"}`;
  const time = at.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  if (sameDay(at, now)) return `at ${time}`;
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  return sameDay(at, tomorrow) ? `tomorrow at ${time}` : `${onDay(at, now)} at ${time}`;
}

/** Why a text could not go yet and when it can, in the sheet's words, or null for other errors. */
export function waitNote(err: unknown, now = new Date()): string | null {
  if (!(err instanceof ApiError) || err.status !== 429 || !err.retryAt) return null;
  const when = retryWhen(err.retryAt, now);
  switch (err.code) {
    case "sms_invite_cooldown":
      return `You just texted this number. You can send another text ${when}.`;
    case "sms_invite_number_daily":
      return `You have texted this number 3 times today. You can send another text ${when}.`;
    case "sms_invite_daily":
      return `You have sent 10 texted invites today. You can send another ${when}.`;
    default:
      return `You can send another text ${when}.`;
  }
}
